/**
 * @file SigningCertificateDropdown.js
 * @description A dropdown component for selecting signing certificates.
 */

import React, { useState, useEffect } from "react";
import { fetchSigningCertificates } from "../../../services/ssoService";
import { Dropdown } from "../../helpers/Dropdown";
import LoadingWrapper from "../../ui/loaders/LoadingWrapper";
import Toast from "../../ui/toasts/Toast";
import { getText } from '../../../i18n';

/**
 * Represents a dropdown component for selecting signing certificates.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {boolean} [props.inlineLabel=true] - Determines whether the label is displayed inline with the dropdown.
 * @param {number} [props.selectedCertificateId] - The ID of the selected signing certificate.
 * @param {function} props.onCertificateChange - The callback function to be called when the selected certificate changes.
 * @param {string} [props.label="Signing Certificate"] - The label text for the dropdown.
 * @param {boolean} [props.isRequired=false] - Determines whether the dropdown is required.
 * @param {boolean} [props.allowNullSelection=false] - Determines whether a null selection is allowed.
 * @returns {JSX.Element} The rendered SigningCertificateDropdown component.
 */
const SigningCertificateDropdown = ({
  className,
  inlineLabel = true,
  selectedCertificateId,
  onCertificateChange,
  label = "Signing Certificate",
  isRequired = false,
  allowNullSelection = false,
}) => {
  const [certificates, setCertificates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getCertificates = async () => {
      setIsLoading(true);
      try {
        const data = await fetchSigningCertificates();
        setCertificates(data);
      } catch (error) {
        console.error("Could not fetch signing certificates:", error);
        Toast.error(getText('msg_7a61c3172947'));
      } finally {
        setIsLoading(false);
      }
    };

    getCertificates();
  }, []);

  const getDefaultLabel = () => {
    const matchingCertificate = certificates.find(
      (cert) => cert.id === selectedCertificateId
    );
    return matchingCertificate
      ? matchingCertificate.name
      : "Select Signing Certificate";
  };

  const certificateOptions = allowNullSelection
    ? [
        { value: null, label: getText('msg_73df0456b12d') },
        ...certificates.map((cert) => ({
          value: cert.id,
          label: cert.name,
        })),
      ]
    : certificates.map((cert) => ({
        value: cert.id,
        label: cert.name,
      }));

  const handleChange = (certificateId) => {
    const selectedCertificate = certificates.find(
      (cert) => cert.id === certificateId
    );
    onCertificateChange(certificateId, selectedCertificate);
  };

  useEffect(() => {
    if (!isLoading && selectedCertificateId && certificates.length > 0) {
      const selectedCertificate = certificates.find(
        (cert) => cert.id === selectedCertificateId
      );
      if (selectedCertificate) {
        onCertificateChange(selectedCertificateId, selectedCertificate);
      }
    }
  }, [isLoading, selectedCertificateId, certificates, onCertificateChange]);

  return (
    <div
      className={`form-group mb-3 ${
        inlineLabel ? "inline-input" : ""
      } ${className}`}
    >
      <label htmlFor="signing-certificate-dropdown">
        {label}
        {isRequired && <span className="text-danger">*</span>}
      </label>
      <LoadingWrapper isLoading={isLoading}>
        <Dropdown
          id="signing-certificate-dropdown"
          defaultValue={getDefaultLabel()}
          options={certificateOptions}
          value={selectedCertificateId}
          onChange={handleChange}
          disabled={certificates.length === 0 || isLoading}
        />
      </LoadingWrapper>
    </div>
  );
};

export default SigningCertificateDropdown;
